import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { LucideIcon } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { COLUMN_PRESETS } from '@/util/constants';

interface SelectionColumnOptions<TData> {
  /** Icon rendered as the resting state of the cell (swaps to the checkbox on hover/focus). */
  getIcon: (row: TData) => LucideIcon;
  /** Human-readable row name, interpolated into the checkbox aria-label. */
  getLabel: (row: TData) => string;
}

/**
 * Leading table column that shows a per-row icon and swaps it for a selection checkbox on row
 * hover or keyboard focus — and pins the checkbox permanently once anything on the page is
 * selected. The swap is opacity-based (never display:none) so the checkbox stays focusable for
 * keyboard users, and a click/tap anywhere on the cell toggles the row for touch devices, which
 * have no hover.
 *
 * Selectability has a single source of truth: `row.getCanSelect()`, i.e. the `enableRowSelection`
 * predicate passed to `DataTable.Root`. Non-selectable rows render the icon only and deliberately
 * do not swallow events, so a row-level `<Link>` keeps navigating.
 */
export const createSelectionColumn = <TData,>({
  getIcon,
  getLabel,
}: SelectionColumnOptions<TData>): ColumnDef<TData> => ({
  id: 'select',
  enableSorting: false,
  enableHiding: false,
  header: ({ table }) => {
    // Nothing on this page can be selected — don't offer a select-all that does nothing.
    if (!table.getRowModel().rows.some((row) => row.getCanSelect())) {
      return null;
    }

    const getCheckedState = () => {
      if (table.getIsAllPageRowsSelected()) {
        return true;
      }
      if (table.getIsSomePageRowsSelected()) {
        return 'indeterminate';
      }
      return false;
    };

    return (
      <div className="flex h-8 w-8 items-center justify-center">
        <Checkbox
          // Stable hook for programmatic focus (e.g. restoring focus after the bulk-delete
          // dialog closes, once its opener has unmounted with the selection).
          data-table-select-all=""
          checked={getCheckedState()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
          aria-label={t('common.select_all_rows')}
        />
      </div>
    );
  },
  cell: ({ row, table }) => {
    const Icon = getIcon(row.original);

    // No event blocking so the <Link> in DataTable can still handle row navigation.
    if (!row.getCanSelect()) {
      return (
        <div className="flex h-8 w-8 items-center justify-center">
          <Icon className="size-4 text-muted-foreground" />
        </div>
      );
    }

    const hasSelectedRows = table.getSelectedRowModel().rows.length > 0;

    return (
      <div
        // The wrapper is a layout box, not a control: the checkbox is the only interactive
        // element. `role="presentation"` says so — giving the div `role="button"`/`tabIndex`
        // instead (as older copies of this column do) puts an inert fake button in the tab order.
        role="presentation"
        className={cn(
          'relative flex h-8 w-8 items-center justify-center',
          !hasSelectedRows && 'group'
        )}
        onClick={(e) => {
          // Keep the click off the row <Link>: stopPropagation blocks React Router's handler,
          // preventDefault blocks the native anchor navigation the click would still trigger.
          e.preventDefault();
          e.stopPropagation();
          // A click on the checkbox itself already toggled. Clicks on the cell chrome (icon /
          // padding) toggle too — the only tap target touch devices get, since they never hover.
          if (!(e.target as HTMLElement).closest('[role="checkbox"]')) {
            row.toggleSelected();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            // stopPropagation only — preventDefault here would cancel the checkbox button's
            // native Space activation and break keyboard toggling.
            e.stopPropagation();
          }
        }}
      >
        {!hasSelectedRows && (
          <Icon className="pointer-events-none absolute inset-0 m-auto size-4 text-muted-foreground group-focus-within:opacity-0 group-hover:opacity-0" />
        )}
        <Checkbox
          // Opacity, not display — a display:none checkbox is unfocusable and would lock
          // keyboard users out of making the first selection.
          className={
            hasSelectedRows
              ? undefined
              : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
          }
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => row.toggleSelected(Boolean(checked))}
          aria-label={t('common.select_row_name', { 0: getLabel(row.original) })}
        />
      </div>
    );
  },
  ...COLUMN_PRESETS.SM,
  meta: { preset: 'SM' },
});
